import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { fetchTransactions } from "../../Apis/Wallet-api";
import WalletListFilters, {
  EMPTY_LIST_FILTERS,
} from "../../Components/Wallet/WalletListFilters";
import WalletPageShell from "../../Components/Wallet/WalletPageShell";
import WalletSelect from "../../Components/Wallet/WalletSelect";
import { formatDateTime, formatLabel, formatMoney } from "../../Utils/walletFormat";

const DEFAULT_PER_PAGE = 20;

export default function WalletTransactionsPage() {
  const navigate = useNavigate();
  const [walletId, setWalletId] = useState("");
  const [draftFilters, setDraftFilters] = useState(EMPTY_LIST_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_LIST_FILTERS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], pagination: {} });

  const load = useCallback(async () => {
    if (!walletId) {
      setData({ items: [], pagination: {} });
      return;
    }
    setLoading(true);
    try {
      setData(await fetchTransactions(walletId, page, perPage, appliedFilters));
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to load transactions."
      );
    } finally {
      setLoading(false);
    }
  }, [walletId, page, perPage, appliedFilters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleWalletChange = (id) => {
    setWalletId(id);
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_LIST_FILTERS);
    setAppliedFilters(EMPTY_LIST_FILTERS);
    setPage(1);
  };

  const total = data.pagination?.total ?? 0;

  return (
    <WalletPageShell
      heading="Wallet transactions"
      subtitle="All money movements for a selected wallet"
    >
      <Stack spacing={2}>
        <WalletSelect value={walletId} onChange={handleWalletChange} />
        <WalletListFilters
          variant="transactions"
          value={draftFilters}
          onChange={setDraftFilters}
          onApply={applyFilters}
          onClear={clearFilters}
        />
        {!walletId ? (
          <Typography color="text.secondary">
            Select a wallet to view transactions.
          </Typography>
        ) : loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary">
              {total} transaction{total === 1 ? "" : "s"}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Gateway ref</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.items || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary">
                          No transactions match these filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (data.items || []).map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(
                            `/wallet/transactions/${walletId}/${row.id}`
                          )
                        }
                      >
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{formatLabel(row.type)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={formatLabel(row.status)} />
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(row.amount)}
                        </TableCell>
                        <TableCell>{row.gateway_reference || "—"}</TableCell>
                        <TableCell>{formatDateTime(row.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={Math.max(0, (data.pagination?.page ?? page) - 1)}
                onPageChange={(_, nextPage) => setPage(nextPage + 1)}
                rowsPerPage={data.pagination?.per_page ?? perPage}
                onRowsPerPageChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </TableContainer>
          </>
        )}
      </Stack>
    </WalletPageShell>
  );
}
